import { Paper, Typography } from '@shared/ui';
import React, { useEffect, useRef } from 'react';
import { getWidgetRenderer } from './registry';
import type {
  DeepPartial,
  MessageWidgetStyles,
  MultipleChoiceWidgetStyles,
  TimelineEntity,
  ToolWidgetStyles,
} from './types';
import './registerAll';

export interface TimelineProps {
  entities: TimelineEntity[];
  autoScroll?: boolean;
  debugMode?: boolean;
  hideRoleLabels?: boolean;
  hideTimestamps?: boolean;
  convId?: string;
  userBgColor?: string;
  assistantBgColor?: string;
  toolWidgetStyles?: DeepPartial<ToolWidgetStyles>;
  messageWidgetStyles?: DeepPartial<MessageWidgetStyles>;
  multipleChoiceWidgetStyles?: DeepPartial<MultipleChoiceWidgetStyles>;
}

function renderEntity(
  entity: TimelineEntity,
  debugMode: boolean,
  hideRoleLabels: boolean,
  hideTimestamps: boolean,
  convId?: string,
  userBgColor?: string,
  assistantBgColor?: string,
  toolWidgetStyles?: DeepPartial<ToolWidgetStyles>,
  messageWidgetStyles?: DeepPartial<MessageWidgetStyles>,
  multipleChoiceWidgetStyles?: DeepPartial<MultipleChoiceWidgetStyles>
): React.ReactNode {
  const Renderer = getWidgetRenderer(entity.kind);
  if (Renderer) {
    return Renderer({
      entity: entity as any,
      debugMode,
      hideRoleLabels,
      hideTimestamps,
      convId,
      userBgColor,
      assistantBgColor,
      toolWidgetStyles,
      messageWidgetStyles,
      multipleChoiceWidgetStyles,
    });
  }
  // Unknown entity type fallback
  return (
    <Paper key={(entity as any).id} variant="outlined" className="p-3 w-full min-w-0 bg-gray-50">
      <Typography variant="caption" color="error">
        Unknown entity type: {(entity as any).kind}
      </Typography>
      <div className="overflow-x-auto max-w-full mt-2">
        <pre className="text-xs overflow-x-auto max-h-32 whitespace-pre">
          {JSON.stringify(entity, null, 2)}
        </pre>
      </div>
    </Paper>
  );
}

export function Timeline({
  entities,
  autoScroll = true,
  debugMode = false,
  hideRoleLabels = false,
  hideTimestamps = false,
  convId,
  userBgColor,
  assistantBgColor,
  toolWidgetStyles,
  messageWidgetStyles,
  multipleChoiceWidgetStyles,
}: TimelineProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new entities arrive
  // biome-ignore lint/correctness/useExhaustiveDependencies: The entities dependency is implicit
  useEffect(() => {
    if (autoScroll && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [entities.length, autoScroll]);

  if (entities.length === 0) {
    return (
      <Paper className="p-8 text-center bg-gray-50">
        <Typography variant="body2" color="text.secondary">
          No messages yet. Start a conversation!
        </Typography>
      </Paper>
    );
  }

  return (
    <div ref={containerRef} className="flex flex-col gap-3 w-full min-w-0">
      {entities.map((entity) =>
        renderEntity(
          entity,
          debugMode,
          hideRoleLabels,
          hideTimestamps,
          convId,
          userBgColor,
          assistantBgColor,
          toolWidgetStyles,
          messageWidgetStyles,
          multipleChoiceWidgetStyles
        )
      )}
    </div>
  );
}
